<script setup lang="ts">
import {
    Bot,
    Check,
    X,
    Trash2,
    Inbox,
    FileText,
    Sparkles,
} from 'lucide-vue-next';

// Documents travelling down the line. Negative delays pre-fill the belt so it
// looks busy from the first frame. Static --x spreads them when motion is off.
const docs = [
    { verdict: 'include', d: '0s', x: '12%' },
    { verdict: 'exclude', d: '-1.4s', x: '26%' },
    { verdict: 'include', d: '-2.8s', x: '40%' },
    { verdict: 'include', d: '-4.2s', x: '58%' },
    { verdict: 'exclude', d: '-5.6s', x: '74%' },
];
</script>

<template>
    <div
        class="line-scene relative aspect-[100/58] min-h-[210px] w-full overflow-hidden rounded-3xl border border-cyan-400/20 bg-slate-950 shadow-2xl"
    >
        <!-- Blueprint grid -->
        <div
            class="pointer-events-none absolute inset-0 opacity-60"
            :style="{
                backgroundImage:
                    'linear-gradient(to right, rgba(56,189,248,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(56,189,248,0.06) 1px, transparent 1px)',
                backgroundSize: '26px 26px',
            }"
        ></div>
        <div
            class="pointer-events-none absolute -top-20 left-1/3 h-56 w-56 rounded-full bg-cyan-400/10 blur-3xl"
        ></div>

        <!-- Circuit / conveyor paths -->
        <svg
            class="absolute inset-0 h-full w-full"
            viewBox="0 0 100 58"
            preserveAspectRatio="none"
            aria-hidden="true"
            fill="none"
        >
            <!-- feed belt -->
            <line
                x1="8"
                y1="29"
                x2="44"
                y2="29"
                stroke="rgba(56,189,248,0.35)"
                stroke-width="0.6"
                stroke-dasharray="2 2"
                class="dash dash-main"
            />
            <!-- include branch -->
            <path
                d="M44 29 L86 15"
                stroke="rgba(34,197,94,0.5)"
                stroke-width="0.6"
                stroke-dasharray="2 2"
                class="dash dash-include"
            />
            <!-- exclude branch -->
            <path
                d="M44 29 L84 49"
                stroke="rgba(239,68,68,0.5)"
                stroke-width="0.6"
                stroke-dasharray="2 2"
                class="dash dash-exclude"
            />
        </svg>

        <!-- Inbox -->
        <div
            class="endpoint absolute top-1/2 left-[8%] -translate-x-1/2 -translate-y-1/2"
        >
            <div
                class="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-slate-300"
            >
                <Inbox class="h-5 w-5" />
            </div>
            <p class="endpoint-label text-slate-400">À trier</p>
        </div>

        <!-- Robot / AI assistant -->
        <div
            class="absolute top-1/2 left-[44%] z-20 -translate-x-1/2 -translate-y-1/2"
        >
            <!-- alternating advice chips -->
            <div
                class="absolute -top-9 left-1/2 -translate-x-1/2 text-[10px] font-semibold whitespace-nowrap"
            >
                <span
                    class="verdict verdict-a absolute left-1/2 -translate-x-1/2 rounded-full border border-emerald-400/40 bg-emerald-400/15 px-2 py-0.5 text-emerald-300"
                >
                    Inclure ?
                </span>
                <span
                    class="verdict verdict-b absolute left-1/2 -translate-x-1/2 rounded-full border border-red-400/40 bg-red-400/15 px-2 py-0.5 text-red-300"
                >
                    Exclure ?
                </span>
            </div>

            <div class="relative">
                <span class="scan-ring absolute inset-0 rounded-2xl"></span>
                <div
                    class="relative flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-400/50 bg-cyan-400/15 text-cyan-200 shadow-[0_0_28px_-4px_rgba(34,211,238,0.6)]"
                >
                    <Bot class="h-6 w-6" />
                </div>
            </div>
            <p
                class="endpoint-label flex items-center justify-center gap-1 text-cyan-200"
            >
                <Sparkles class="h-3 w-3" />
                Assistant IA
            </p>
        </div>

        <!-- Include tray -->
        <div
            class="endpoint absolute top-[26%] left-[86%] -translate-x-1/2 -translate-y-1/2"
        >
            <div
                class="flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
            >
                <Check class="h-5 w-5" />
            </div>
            <p class="endpoint-label text-emerald-300">Inclus</p>
        </div>

        <!-- Trash / exclude -->
        <div
            class="endpoint absolute top-[84%] left-[84%] -translate-x-1/2 -translate-y-1/2"
        >
            <div
                class="flex h-12 w-12 items-center justify-center rounded-2xl border border-red-400/40 bg-red-400/10 text-red-300"
            >
                <Trash2 class="h-5 w-5" />
            </div>
            <p class="endpoint-label text-red-300">Exclus</p>
        </div>

        <!-- Travelling documents -->
        <div
            v-for="(doc, i) in docs"
            :key="i"
            class="doc"
            :class="doc.verdict === 'include' ? 'doc--include' : 'doc--exclude'"
            :style="{ '--d': doc.d, '--x': doc.x }"
            aria-hidden="true"
        >
            <FileText class="doc-watermark" />
            <span class="doc-line"></span>
            <span class="doc-line w-2/3"></span>
            <span class="doc-line w-1/2"></span>
            <span
                class="doc-badge"
                :class="
                    doc.verdict === 'include'
                        ? 'border-emerald-400/60 bg-emerald-500 text-white'
                        : 'border-red-400/60 bg-red-500 text-white'
                "
            >
                <Check v-if="doc.verdict === 'include'" class="h-3 w-3" />
                <X v-else class="h-3 w-3" />
            </span>
        </div>

        <!-- Caption -->
        <p
            class="absolute bottom-3 left-1/2 -translate-x-1/2 text-center text-[11px] text-slate-500"
        >
            L'IA propose, vous décidez — chaque article est inclus ou exclu avec
            sa justification.
        </p>
    </div>
</template>

<style scoped>
.endpoint {
    z-index: 10;
    text-align: center;
}

.endpoint-label {
    margin-top: 0.4rem;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
}

/* Document card */
.doc {
    position: absolute;
    top: 50%;
    left: var(--x, 8%);
    z-index: 15;
    display: flex;
    width: clamp(34px, 9%, 50px);
    aspect-ratio: 3 / 4;
    transform: translate(-50%, -50%);
    flex-direction: column;
    justify-content: center;
    gap: 3px;
    border: 1px solid rgba(148, 163, 184, 0.3);
    border-radius: 6px;
    background: #0f172a;
    padding: 6px;
}

.doc-watermark {
    position: absolute;
    top: 4px;
    right: 4px;
    height: 10px;
    width: 10px;
    color: rgba(148, 163, 184, 0.35);
}

.doc-line {
    height: 2px;
    width: 100%;
    border-radius: 9999px;
    background: rgba(148, 163, 184, 0.45);
}

.doc-badge {
    position: absolute;
    top: -6px;
    left: -6px;
    display: flex;
    height: 18px;
    width: 18px;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    border-width: 1px;
    opacity: 0;
}

.scan-ring {
    border: 1px solid rgba(34, 211, 238, 0.5);
}

@media (prefers-reduced-motion: no-preference) {
    .doc--include {
        animation: doc-include 7s linear infinite;
        animation-delay: var(--d);
    }

    .doc--exclude {
        animation: doc-exclude 7s linear infinite;
        animation-delay: var(--d);
    }

    .doc-badge {
        animation: badge-in 7s linear infinite;
        animation-delay: var(--d);
    }

    .dash {
        animation: dash-flow 1s linear infinite;
    }

    .scan-ring {
        animation: scan 2.4s ease-in-out infinite;
    }

    .verdict-a {
        animation: verdict-a 3.6s ease-in-out infinite;
    }

    .verdict-b {
        animation: verdict-b 3.6s ease-in-out infinite;
    }

    @keyframes doc-include {
        0% {
            left: 8%;
            top: 50%;
            opacity: 0;
            background: #0f172a;
            border-color: rgba(148, 163, 184, 0.3);
        }
        5% {
            opacity: 1;
        }
        38% {
            left: 44%;
            top: 50%;
            background: #0f172a;
            border-color: rgba(148, 163, 184, 0.3);
        }
        48% {
            left: 44%;
            top: 50%;
        }
        57% {
            background: rgba(16, 185, 129, 0.2);
            border-color: rgba(16, 185, 129, 0.75);
        }
        84% {
            left: 86%;
            top: 26%;
            opacity: 1;
            background: rgba(16, 185, 129, 0.2);
            border-color: rgba(16, 185, 129, 0.75);
        }
        94% {
            left: 86%;
            top: 26%;
            opacity: 0;
        }
        100% {
            opacity: 0;
        }
    }

    @keyframes doc-exclude {
        0% {
            left: 8%;
            top: 50%;
            opacity: 0;
            background: #0f172a;
            border-color: rgba(148, 163, 184, 0.3);
            transform: translate(-50%, -50%) rotate(0deg);
        }
        5% {
            opacity: 1;
        }
        38% {
            left: 44%;
            top: 50%;
            transform: translate(-50%, -50%) rotate(0deg);
        }
        48% {
            left: 44%;
            top: 50%;
        }
        57% {
            background: rgba(239, 68, 68, 0.2);
            border-color: rgba(239, 68, 68, 0.75);
        }
        84% {
            left: 84%;
            top: 84%;
            opacity: 1;
            background: rgba(239, 68, 68, 0.2);
            border-color: rgba(239, 68, 68, 0.75);
            transform: translate(-50%, -50%) rotate(10deg) scale(0.7);
        }
        94% {
            left: 84%;
            top: 84%;
            opacity: 0;
            transform: translate(-50%, -50%) rotate(10deg) scale(0.5);
        }
        100% {
            opacity: 0;
        }
    }

    @keyframes badge-in {
        0%,
        52% {
            opacity: 0;
        }
        58%,
        88% {
            opacity: 1;
        }
        96%,
        100% {
            opacity: 0;
        }
    }

    @keyframes dash-flow {
        to {
            stroke-dashoffset: -4;
        }
    }

    @keyframes scan {
        0%,
        100% {
            opacity: 0;
            transform: scale(1);
        }
        50% {
            opacity: 1;
            transform: scale(1.35);
        }
    }

    @keyframes verdict-a {
        0%,
        42% {
            opacity: 1;
        }
        50%,
        92% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }

    @keyframes verdict-b {
        0%,
        42% {
            opacity: 0;
        }
        50%,
        92% {
            opacity: 1;
        }
        100% {
            opacity: 0;
        }
    }
}
</style>
